import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

interface FeatureFlagContextType {
  flags: Record<string, boolean>;
  isLoading: boolean;
  isEnabled: (flagKey: string) => boolean | undefined;
  refetchFlags: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({
    booking_enabled: false,
    credits_purchase_enabled: false,
    advertisements_global: false,
    // AI flags (#236 Phase A1). Defaults are FALSE so any AI surface gated
    // on these is hidden on first render before /feature-flags resolves.
    // Backend orchestrator does its own DB-level gate; these defaults only
    // affect frontend visibility during the brief loading window.
    ai_global: false,
    ai_trip_planner: false,
    ai_trip_import: false,
    ai_photo_analysis: false,
    ai_activity_recommendations: false,
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchFlags = async () => {
    try {
      const response = await fetch(`${API_URL}/feature-flags`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.flags) {
          setFlags(data.flags);
        }
      }
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFlags();
  }, []);

  const isEnabled = (flagKey: string): boolean | undefined => {
    if (!(flagKey in flags)) {
      return undefined;
    }
    return flags[flagKey] === true;
  };

  const refetchFlags = async () => {
    setIsLoading(true);
    await fetchFlags();
  };

  return (
    <FeatureFlagContext.Provider value={{ flags, isLoading, isEnabled, refetchFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagContextType => {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
};

// Convenience hook for checking a single flag
export const useFeatureFlag = (flagKey: string): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagKey) === true;
};
